/**
 * Ce qui s'affiche quand aucune route ne correspond à l'adresse.
 *
 * Avant le 18-08-2026 : RIEN. Le routeur n'avait pas de route attrape-tout,
 * donc une adresse inconnue rendait une page BLANCHE — sans un mot, sans un
 * lien de retour, sans rien à faire. On ne pouvait même pas savoir si c'était
 * une faute de frappe, une panne, ou une page pas encore chargée.
 *
 * 🪤 Et le cas le plus fréquent n'est PAS la faute de frappe : c'est une
 * adresse qui existe DÉJÀ en ligne mais pas encore dans le cockpit du
 * visiteur. Le cockpit est une PWA — son service worker sert l'ancien bundle
 * jusqu'à ce qu'on accepte la nouvelle version. Une route fraîchement
 * déployée est donc invisible pour qui n'a pas rechargé, et la page blanche
 * était son seul symptôme. Payé le 18-08 : `/regie-test-vente` déployée,
 * servie, vérifiée sur le bundle — et blanche chez Kayla.
 *
 * D'où le bouton : il ne se contente pas de recharger (un rechargement normal
 * repasse par le service worker et re-sert le même vieux bundle). Il PURGE
 * les caches, désenregistre les service workers, puis recharge. C'est le seul
 * geste qui marche aussi dans l'app installée, où « Ctrl+Shift+R » n'existe
 * pas.
 */
import { useState } from "react";

import { ENTETE_ECRAN } from "@/core/auth/enteteEcran";
import { Button, Carte, EnTeteDePanneau } from "@/shared/ui";

async function purgerEtRecharger(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const sws = await navigator.serviceWorker.getRegistrations();
      await Promise.all(sws.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const noms = await caches.keys();
      await Promise.all(noms.map((n) => caches.delete(n)));
    }
  } catch {
    // Purge best-effort : si le navigateur refuse, le rechargement dur
    // ci-dessous reste la meilleure chance qu'on ait.
  }
  window.location.replace(window.location.pathname + window.location.search);
}

export function AdresseInconnue() {
  const [enCours, setEnCours] = useState(false);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas px-4 py-10 text-fg">
      <div className="w-full max-w-[520px]">
        <Carte remplissage="lg" className="sm:p-10">
          <div className="flex flex-col items-center gap-4 text-center">
            <EnTeteDePanneau titre="Cette page n'existe pas ici" className={ENTETE_ECRAN} />
            <p className="text-[14px] leading-relaxed text-muted">
              Soit l'adresse est fausse, soit elle a été mise en ligne après le
              chargement de ton cockpit. Le cockpit garde sa version en mémoire —
              tant qu'il ne l'a pas remplacée, une page toute neuve reste invisible.
            </p>
            <code className="max-w-full break-all font-mono text-[12.5px] text-fg">
              {window.location.pathname}
            </code>
          </div>

          <div className="mt-8 flex flex-col gap-2.5 sm:flex-row sm:justify-center">
            <Button
              variant="accent"
              size="xl"
              disabled={enCours}
              onClick={() => {
                setEnCours(true);
                void purgerEtRecharger();
              }}
              className="w-full sm:w-auto"
            >
              {enCours ? "Mise à jour…" : "Charger la dernière version"}
            </Button>
            <Button
              variant="secondary"
              size="xl"
              onClick={() => window.location.assign("/")}
              className="w-full sm:w-auto"
            >
              Retour au cockpit
            </Button>
          </div>
        </Carte>
      </div>
    </div>
  );
}
